import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import Input from './Input';
import Button from './Button';
import SearchableSelect from './SearchableSelect';

const createEmptyLineItem = () => ({ productId: '', totalWeight: '' });

const DDPLineItemsEditor = ({
  t,
  lineItems,
  setLineItems,
  productOptions,
  productsLoading = false,
  disabled = false,
  errors = {}
}) => {
  const updateLineItem = (index, key, value) => {
    setLineItems((prev) => prev.map((item, idx) => (
      idx === index ? { ...item, [key]: value } : item
    )));
  };

  const addLineItem = () => {
    setLineItems((prev) => [...prev, createEmptyLineItem()]);
  };

  const removeLineItem = (index) => {
    setLineItems((prev) => {
      if (prev.length <= 1) {
        return [createEmptyLineItem()];
      }
      return prev.filter((_, idx) => idx !== index);
    });
  };

  const totalWeight = lineItems.reduce((sum, item) => {
    const value = Number(item.totalWeight);
    return sum + (Number.isFinite(value) ? value : 0);
  }, 0);

  const getDisplayUnit = (unit) => {
    const normalizedUnit = String(unit || '').toLowerCase();
    if (!normalizedUnit) return t('products.form.units.ton', { defaultValue: 'tonne' });

    if (normalizedUnit === 'kg') {
      return t('products.form.units.ton', { defaultValue: 'tonne' });
    }

    return t(`products.form.units.${normalizedUnit}`, { defaultValue: unit });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">{t('ddp.lineItems')}</h3>
      </div>

      <div className="space-y-3">
        {lineItems.map((item, index) => (
          <div key={`ddp-line-${index}`} className="rounded-lg border border-gray-200 p-4">
            {(() => {
              const selectedProduct = productOptions.find((option) => option.value === item.productId);
              const unitLabel = getDisplayUnit(selectedProduct?.unit);

              return (
            <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_220px_auto] gap-3 items-start">
              <SearchableSelect
                label={t('ddp.product')}
                name={`productId-${index}`}
                value={item.productId}
                onChange={(e) => updateLineItem(index, 'productId', e.target.value)}
                options={productOptions}
                placeholder={t('ddp.selectProduct')}
                required
                disabled={disabled || productsLoading}
                error={errors[`lineItems.${index}.productId`]}
              />

              <Input
                label={`${t('ddp.totalWeight')} (${unitLabel})`}
                name={`totalWeight-${index}`}
                type="number"
                min="0"
                step="0.01"
                value={item.totalWeight}
                onChange={(e) => updateLineItem(index, 'totalWeight', e.target.value)}
                required
                disabled={disabled}
                error={errors[`lineItems.${index}.totalWeight`]}
              />

              <div className="pt-7">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeLineItem(index)}
                disabled={disabled}
                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                title={t('ddp.removeLineItem')}
              >
                <TrashIcon className="h-4 w-4" />
              </Button>
              </div>
            </div>
              );
            })()}
          </div>
        ))}
      </div>

      <div className="w-full">
        <Button
          type="button"
          size="sm"
          onClick={addLineItem}
          disabled={disabled}
          className="w-full justify-center bg-green-600 text-white hover:bg-green-700"
        >
          {t('ddp.addLineItem')}
        </Button>
      </div>

      <div className="rounded-lg bg-blue-50 border border-blue-100 p-3 text-sm text-blue-900">
        <span className="font-medium">{t('ddp.derivedTotalWeight')}:</span>{' '}
        {totalWeight.toLocaleString('fr-FR')} t
      </div>

      {errors.lineItems && (
        <p className="text-sm text-red-600">{errors.lineItems}</p>
      )}
    </div>
  );
};

export { createEmptyLineItem };
export default DDPLineItemsEditor;
